// Project modules
import {
	logger,
} from '@config/logger';
import {
	SESSION_KEY_PREFIX_OIDC_USER,
} from '@config/storageKeys';

/**
 * Reads the platform bearer token of the current session, if there is one.
 *
 * Presence of a token is what tells a platform app from one authenticating with a
 * same-origin session cookie (abylaweb/Model, dataroom/Keep). Callers use it as the
 * gate before any call to a platform API: those apps have no token, and a call from
 * them would only produce a 401 and CORS noise in a customer's console.
 *
 * Never throws — an unreadable or malformed entry resolves to undefined.
 *
 * @returns The access token, or undefined when the session has none
 */
export const getOidcAccessToken = (): string | undefined => {
	try {
		const keys = Object.keys(window.sessionStorage).filter((key) => key.startsWith(SESSION_KEY_PREFIX_OIDC_USER));

		for (const key of keys) {
			const raw = window.sessionStorage.getItem(key);
			if (!raw) {
				continue;
			}

			const parsed = JSON.parse(raw);
			// Different OIDC client libraries nest the tokens differently.
			const token = (parsed.authnResult || parsed.authResult || parsed)?.access_token;

			if (token) {
				return token;
			}
		}
	} catch (e) {
		logger.warn('[oidcToken] Could not read the OIDC access token', {
			e,
		});
	}

	return undefined;
};
